import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { VAT_OPTIONS } from '@/components/VatTreatmentFields';

export const ClassifyPurchaseDialog = ({ item, onClose, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => {
    if (item) setF({ vat_treatment: item.review.vat_treatment || '', supplier_vat_number: item.review.supplier_vat_number || item.supplier_vat_on_file || '',
      invoice_number: item.review.invoice_number || item.invoice_number || '', attachment_url: item.review.attachment_url || item.attachment_url || '', notes: item.review.notes || '' });
  }, [item]);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post('/expenses/upload-bill', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setF((x) => ({ ...x, attachment_url: r.data.bill_url })); toast.success('Invoice attached');
    } catch { toast.error('Upload failed'); }
  };
  const save = async () => {
    try {
      await api.put(`/purchase-vat-review/${item.id}`, f);
      toast.success('Classification saved — no accounting change made'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const std = f.vat_treatment === 'standard';
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="classify-purchase-dialog">
        <DialogHeader><DialogTitle>Classify {item?.ref} · {item?.supplier || item?.category}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Gross {item?.gross?.toFixed(2)} · {item?.date} · {item?.branch}. Saved as a review note only — amounts, VAT, supplier balance and journals do not change.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">VAT type (from the supplier invoice) *</Label>
            <select value={f.vat_treatment || ''} onChange={(e) => setF({ ...f, vat_treatment: e.target.value })}
              className="w-full h-9 text-sm border rounded-md px-2 bg-background" data-testid="classify-vat-select">
              <option value="" disabled>Select…</option>
              {VAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Invoice no. {std && '*'}</Label>
              <Input value={f.invoice_number || ''} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} data-testid="classify-invoice-input" /></div>
            {std && <div><Label className="text-xs">Supplier VAT no. *</Label>
              <Input value={f.supplier_vat_number || ''} inputMode="numeric" onChange={(e) => setF({ ...f, supplier_vat_number: e.target.value.replace(/\D/g, '').slice(0, 15) })} data-testid="classify-vat-no-input" /></div>}
          </div>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1 text-xs border rounded-md px-2 h-8 cursor-pointer hover:bg-muted" data-testid="classify-attach-btn">
              <Paperclip size={12} />Attach supplier invoice
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files[0])} />
            </label>
            {f.attachment_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${f.attachment_url}`} target="_blank" rel="noreferrer" className="text-xs text-primary underline" data-testid="classify-attachment-link">View attached</a>}
          </div>
          <div><Label className="text-xs">Notes</Label><Input value={f.notes || ''} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="classify-notes-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!f.vat_treatment} data-testid="classify-save-btn">Save classification</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
